import { useState } from 'react';
import { Link } from 'react-router-dom';

type Engagement = 'sans' | '6mois' | '12mois';

const VITRINE_PLANS = [
  {
    name: 'Essentiel',
    prices: { sans: 69, '6mois': 59, '12mois': 49 },
    color: 'teal',
    icon: 'ri-layout-line',
    popular: false,
    badge: null,
    description: 'Présence en ligne simple et efficace',
    features: [
      'Site vitrine (1 à 3 pages)',
      'Design sur mesure',
      'Optimisation mobile',
      'SEO de base',
      'Formulaire de contact',
      'Hébergement + maintenance inclus',
      'Sécurité + modifications simples',
    ],
  },
  {
    name: 'Pro',
    prices: { sans: 129, '6mois': 99, '12mois': 79 },
    color: 'emerald',
    icon: 'ri-rocket-2-line',
    popular: true,
    badge: '⭐ POPULAIRE',
    description: 'Pour acquérir des clients rapidement',
    features: [
      "Site complet (jusqu'à 5 pages)",
      'Design sur mesure',
      'Optimisation mobile',
      'SEO avancé',
      'Formulaire de contact',
      'Hébergement inclus',
      'Support prioritaire',
    ],
  },
  {
    name: 'Premium',
    prices: { sans: 199, '6mois': 159, '12mois': 129 },
    color: 'amber',
    icon: 'ri-vip-crown-2-line',
    popular: false,
    badge: '👑 PREMIUM',
    description: 'Croissance maximale, visibilité totale',
    features: [
      'Site sur mesure (pages illimitées)',
      'Design avancé',
      'SEO + optimisation conversion',
      'Gestion fiche Google Business',
      'Formulaire de contact',
      'Hébergement inclus',
      'Modifications illimitées',
    ],
  },
];

const MOBILE_LABELS: Record<Engagement, string> = {
  sans: 'Libre',
  '6mois': '6 mois',
  '12mois': '12 mois',
};

const DESKTOP_LABELS: Record<Engagement, string> = {
  sans: 'Sans engagement',
  '6mois': 'Engagement 6 mois',
  '12mois': 'Engagement 12 mois',
};

const SAVINGS: Record<Engagement, string | null> = {
  sans: null,
  '6mois': "Économisez jusqu'à 23%",
  '12mois': "Économisez jusqu'à 35%",
};

export default function VitrineSection() {
  const [engagement, setEngagement] = useState<Engagement>('sans');

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6 bg-slate-50/80">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center mb-8 sm:mb-14">
          <div className="inline-flex items-center space-x-2 bg-teal-50 border border-teal-100 text-teal-700 px-4 py-2 rounded-full text-xs font-bold mb-4">
            <i className="ri-pages-line text-sm"></i>
            <span>SITES VITRINE</span>
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-slate-900 mb-2 sm:mb-4">
            Offres{' '}
            <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">
              Sites Vitrine
            </span>
          </h2>
          <p className="text-sm sm:text-lg text-slate-500 max-w-xl mx-auto">
            Création de site internet à partir de <strong className="text-slate-700">69€/mois</strong> sans engagement
          </p>
        </div>

        {/* Toggle engagement — mobile-first */}
        <div className="flex flex-col items-center mb-8 sm:mb-12 gap-3">
          <div className="w-full max-w-sm sm:w-auto">
            <div className="grid grid-cols-3 sm:inline-flex bg-white border border-slate-100 rounded-2xl p-1.5 gap-1 w-full sm:w-auto shadow-sm">
              {(['sans', '6mois', '12mois'] as Engagement[]).map((e) => (
                <button
                  key={e}
                  onClick={() => setEngagement(e)}
                  className={`flex-1 sm:flex-none sm:px-6 py-3 sm:py-2.5 rounded-xl font-bold transition-all duration-200 cursor-pointer text-center ${
                    engagement === e
                      ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-md'
                      : 'text-slate-600'
                  }`}
                >
                  <span className="block sm:hidden text-xs">{MOBILE_LABELS[e]}</span>
                  <span className="hidden sm:block text-sm">{DESKTOP_LABELS[e]}</span>
                </button>
              ))}
            </div>
          </div>

          {SAVINGS[engagement] && (
            <span className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-1.5 rounded-full text-xs font-bold">
              🎉 {SAVINGS[engagement]}
            </span>
          )}
        </div>

        {/* Cards */}
        <div className="flex flex-col md:grid md:grid-cols-3 gap-5 sm:gap-6">
          {VITRINE_PLANS.map((plan) => {
            const isEmerald = plan.color === 'emerald';
            const isAmber = plan.color === 'amber';

            return (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl sm:rounded-3xl transition-all duration-300 flex flex-col overflow-hidden ${
                  plan.popular
                    ? 'border-2 border-emerald-400 shadow-2xl shadow-emerald-500/15'
                    : isAmber
                    ? 'border-2 border-amber-200 shadow-lg'
                    : 'border-2 border-slate-100 shadow-md'
                }`}
              >
                {/* Badge populaire — bandeau en haut */}
                {plan.badge && (
                  <div
                    className={`w-full py-2.5 text-center text-xs font-black text-white tracking-wide ${
                      plan.popular
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                        : 'bg-gradient-to-r from-amber-500 to-yellow-500'
                    }`}
                  >
                    {plan.badge}
                  </div>
                )}

                <div className="p-5 sm:p-8 flex flex-col flex-1">

                  {/* Icone + titre */}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${
                        plan.popular ? 'bg-emerald-100' : isAmber ? 'bg-amber-100' : 'bg-teal-50'
                      }`}
                    >
                      <i
                        className={`${plan.icon} text-xl ${
                          plan.popular ? 'text-emerald-600' : isAmber ? 'text-amber-600' : 'text-teal-600'
                        }`}
                      ></i>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-black text-slate-900 leading-tight">{plan.name}</h3>
                      <p className="text-xs text-slate-400 leading-tight mt-0.5">{plan.description}</p>
                    </div>
                  </div>

                  {/* Prix */}
                  <div className="flex items-baseline gap-1.5 mb-1">
                    <span
                      className={`text-4xl sm:text-5xl font-black ${
                        plan.popular
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent'
                          : isAmber
                          ? 'text-amber-600'
                          : 'text-teal-600'
                      }`}
                    >
                      {plan.prices[engagement]}€
                    </span>
                    <span className="text-sm text-slate-400 font-medium">/mois</span>
                  </div>
                  {engagement !== 'sans' && (
                    <p className="text-xs text-slate-400 line-through mb-4">
                      {plan.prices.sans}€/mois sans engagement
                    </p>
                  )}
                  {engagement === 'sans' && <div className="mb-4" />}

                  {/* Séparateur */}
                  <div className="border-t border-slate-100 mb-4" />

                  {/* Features */}
                  <div className="grid grid-cols-1 gap-2 mb-6 flex-1">
                    {plan.features.map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div
                          className={`w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${
                            plan.popular ? 'bg-emerald-100' : isAmber ? 'bg-amber-100' : 'bg-teal-50'
                          }`}
                        >
                          <i
                            className={`ri-check-line text-[10px] ${
                              plan.popular ? 'text-emerald-600' : isAmber ? 'text-amber-600' : 'text-teal-600'
                            }`}
                          ></i>
                        </div>
                        <span className="text-xs sm:text-sm text-slate-700 leading-snug">{f}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Link
                    to="/paiement"
                    className={`w-full flex items-center justify-center gap-2 py-3.5 sm:py-4 rounded-xl text-sm font-bold transition-all duration-300 active:scale-95 hover:scale-[1.02] whitespace-nowrap cursor-pointer ${
                      plan.popular
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:shadow-lg hover:shadow-emerald-500/30'
                        : isAmber
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:shadow-lg hover:shadow-amber-500/20'
                        : 'bg-teal-50 text-teal-700 border-2 border-teal-200 hover:bg-teal-100'
                    }`}
                  >
                    <span>S&apos;abonner maintenant</span>
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Livraison */}
        <div className="mt-7 flex items-center justify-center gap-2 text-sm text-slate-500 text-center">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-timer-flash-line text-emerald-500 text-lg"></i>
          </div>
          <span>
            Livraison de votre site en{' '}
            <strong className="text-slate-900">7 jours maximum</strong> après validation
          </span>
        </div>
      </div>
    </section>
  );
}
